import { useState, useEffect } from "react";
import { Upload, Save, Key, Building2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [clinicName, setClinicName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/admin`, {
        withCredentials: true,
      });
      setClinicName(response.data.clinic_name || "");
      setApiKey(response.data.feegow_api_key || "");
      setLogoUrl(response.data.logo_url || null);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/settings`,
        {
          clinic_name: clinicName,
          feegow_api_key: apiKey,
        },
        { withCredentials: true }
      );
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Por favor, selecione uma imagem");
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", logoFile);

      const response = await axios.post(
        `${API_URL}/api/settings/upload-logo`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        setLogoUrl(response.data.logo_url);
        setLogoFile(null);
        setLogoPreview(null);
        toast.success("Logo atualizado com sucesso!");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Erro ao enviar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">
            Configurações
          </h1>
          <p className="text-slate-500 mt-1">
            Configure os dados da clínica e integração Feegow
          </p>
        </div>

        <div className="space-y-8">
          {/* Clinic Info */}
          <div className="admin-card">
            <h2 className="text-xl font-semibold text-slate-800 mb-6 font-['Outfit'] flex items-center gap-3">
              <Building2 className="w-6 h-6 text-[#00357C]" />
              Dados da Clínica
            </h2>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="clinicName">Nome da Clínica</Label>
                <Input
                  id="clinicName"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  placeholder="Nome da sua clínica"
                  className="h-12"
                  data-testid="clinic-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Logo da Clínica</Label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center overflow-hidden">
                    {logoPreview || logoUrl ? (
                      <img
                        src={logoPreview || (logoUrl.startsWith("http") ? logoUrl : `${API_URL}${logoUrl}`)}
                        alt="Logo"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="w-10 h-10 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="logo-upload"
                      data-testid="logo-upload-input"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg cursor-pointer transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Selecionar Imagem
                    </label>
                    {logoFile && (
                      <Button
                        onClick={handleUploadLogo}
                        disabled={uploadingLogo}
                        className="ml-2 bg-[#00357C] hover:bg-[#002d59]"
                        data-testid="upload-logo-btn"
                      >
                        {uploadingLogo ? "Enviando..." : "Enviar"}
                      </Button>
                    )}
                    <p className="text-sm text-slate-500 mt-2">
                      Formatos: PNG, JPG, WEBP. Tamanho recomendado: 200x200px
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* API Integration */}
          <div className="admin-card">
            <h2 className="text-xl font-semibold text-slate-800 mb-6 font-['Outfit'] flex items-center gap-3">
              <Key className="w-6 h-6 text-[#00357C]" />
              Integração Feegow
            </h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key (x-access-token)</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Cole aqui sua chave de API do Feegow"
                  className="h-12 font-mono"
                  data-testid="api-key-input"
                />
                <p className="text-sm text-slate-500">
                  Obtenha sua chave em:{" "}
                  <a
                    href="https://docs.feegow.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00357C] hover:underline"
                  >
                    docs.feegow.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            className="w-full h-12 bg-[#00357C] hover:bg-[#002d59]"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Salvando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-5 h-5" />
                Salvar Configurações
              </span>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
